import { useEffect, useState, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { useRBAC } from '@/lib/rbac/context'
import { PersonPickerBar } from '@/components/performance/bonificacao/PersonPickerBar'
import { StatusBadge } from '@/components/performance/bonificacao/StatusBadge'
import { CategoryAccordion } from '@/components/performance/bonificacao/CategoryAccordion'
import { GateBadge } from '@/components/performance/bonificacao/GateBadge'
import { BonusSummaryTable } from '@/components/performance/bonificacao/BonusSummaryTable'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import {
  useBonusAssessment,
  useBonusCalculate,
  useAdvanceStatus,
  useRevertStatus,
  useUpdateIndicador,
  useProaPercent,
  useToggleCategoryPublish,
} from '@/api/bonificacao-hooks'
import {
  getFieldPermissions,
  canEdit,
  isDirectorRole,
  isRHRole,
} from '@/lib/bonificacao/permissions'
import { useIndicatorAutoSave } from '@/components/performance/bonificacao/useIndicatorAutoSave'
import { Skeleton } from '@/components/ui/skeleton'
import { Lock, Target, ChevronDown, ArrowLeft } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import type { BonusAssessmentStatus, OKRKeyResult } from '@/components/performance/bonificacao/types'

interface MetasSearch {
  employee?: string
}

const STATUS_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  em_apuracao: 'Em Apuração',
  validacao_rh: 'Validação RH',
  finalizado: 'Finalizado',
}

const ADVANCE_LABELS: Record<string, string> = {
  rascunho: 'Avançar',
  em_apuracao: 'Enviar p/ RH',
  validacao_rh: 'Finalizar',
}

const NEXT_STATUS: Record<string, string> = {
  rascunho: 'em_apuracao',
  em_apuracao: 'validacao_rh',
  validacao_rh: 'finalizado',
}

const PREV_STATUS: Record<string, string> = {
  em_apuracao: 'rascunho',
  validacao_rh: 'em_apuracao',
  finalizado: 'validacao_rh',
}

function MetasPage() {
  const { employee } = Route.useSearch()
  const setSelectedEmployee = useBonificacaoStore((s) => s.setSelectedEmployee)
  const currentAssessmentId = useBonificacaoStore((s) => s.currentAssessmentId)
  const navigate = useNavigate()
  const { user } = useRBAC()
  const userRole = user?.role ?? ''

  useEffect(() => {
    if (employee) {
      setSelectedEmployee(employee)
    }
  }, [])

  // Data queries
  const {
    data: assessment,
    isLoading: isLoadingAssessment,
  } = useBonusAssessment(currentAssessmentId)

  const {
    data: calculateResult,
    isLoading: isLoadingCalc,
  } = useBonusCalculate(currentAssessmentId)

  // Mutations
  const advanceMutation = useAdvanceStatus()
  const revertMutation = useRevertStatus()
  const updateIndicadorMutation = useUpdateIndicador()
  const togglePublishMutation = useToggleCategoryPublish()

  const isRH = isRHRole(userRole)

  // Auto-save hook for indicator updates
  const { handleUpdate: handleIndicatorUpdate, isSaving } = useIndicatorAutoSave({
    assessmentId: currentAssessmentId,
    onMutate: (payload) => updateIndicadorMutation.mutate(payload),
  })

  // PROA auto-fill: populate PROA indicator with % PROA from DRE data
  const { data: proaData } = useProaPercent(assessment?.year ?? 2025)
  const [proaAutoFilled, setProaAutoFilled] = useState(false)

  useEffect(() => {
    if (!assessment || !proaData?.percent || !currentAssessmentId || proaAutoFilled) return
    // Find PROA category (name contains "PROA")
    const proaCat = assessment.template?.categorias?.find(
      (c) => c.nome.toUpperCase().includes('PROA')
    )
    if (!proaCat) return
    // Find first indicator in PROA category
    const proaIndicador = proaCat.indicadores?.[0]
    if (!proaIndicador) return
    // Check if indicator value is empty
    const dados = assessment.dados?.[proaIndicador.id]
    if (dados?.valor != null && dados.valor > 0) return // already filled
    // Auto-fill with % PROA
    updateIndicadorMutation.mutate({
      assessmentId: currentAssessmentId,
      indicadorId: proaIndicador.id,
      dados: { valor: proaData.percent },
    })
    setProaAutoFilled(true)
  }, [assessment, proaData, currentAssessmentId, proaAutoFilled])

  // OKR expand/collapse state for Category 3
  const [expandedOkrs, setExpandedOkrs] = useState<Set<string>>(new Set())
  const toggleOkr = (okrId: string) => {
    setExpandedOkrs((prev) => {
      const next = new Set(prev)
      if (next.has(okrId)) {
        next.delete(okrId)
      } else {
        next.add(okrId)
      }
      return next
    })
  }

  // Dialog state
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false)
  const [revertDialogOpen, setRevertDialogOpen] = useState(false)
  const [advanceValidationError, setAdvanceValidationError] = useState<string | null>(null)

  // Permissions
  const status = assessment?.status as BonusAssessmentStatus | undefined
  const showAdvance = status ? canEdit('advanceStatus', status, userRole) : false
  const showRevert = status ? canEdit('revertStatus', status, userRole) : false
  const isDirector = isDirectorRole(userRole)
  const isOwnAssessment = assessment?.employeeId === user?.employee?.employeeId

  const permissions = useMemo(() => {
    if (!status) return null
    return getFieldPermissions(status, userRole, isOwnAssessment)
  }, [status, userRole, isOwnAssessment])

  const readOnly = isDirector && !permissions?.valor

  // Gate resolution helper
  const getCategoryGateInfo = (catId: string) => {
    const cat = assessment?.template?.categorias?.find((c) => c.id === catId)
    if (!cat?.gateCat) return null
    const prereqResult = calculateResult?.categorias?.find((c) => c.id === cat.gateCat)
    const prereqCat = assessment?.template?.categorias?.find((c) => c.id === cat.gateCat)
    return {
      isBlocked: prereqResult ? prereqResult.media < (cat.gateMin ?? 0) : false,
      prereqName: prereqCat?.nome ?? cat.gateCat,
      gateMin: cat.gateMin ?? 0,
    }
  }

  // Handlers
  const handleAdvance = () => {
    if (!currentAssessmentId) return

    // Validate: at least one indicator must have data filled when advancing from rascunho
    if (assessment?.status === 'rascunho') {
      const dados = assessment.dados ?? {}
      const hasAnyData = Object.values(dados).some(
        (d) => d && (d.valor !== null && d.valor !== undefined || d.comprovado)
      )
      if (!hasAnyData) {
        setAdvanceValidationError(
          'Preencha pelo menos um indicador antes de avançar a avaliação.'
        )
        return
      }
    }

    setAdvanceValidationError(null)
    advanceMutation.mutate(currentAssessmentId, {
      onSuccess: () => setAdvanceDialogOpen(false),
    })
  }

  const handleRevert = () => {
    if (!currentAssessmentId) return
    revertMutation.mutate(currentAssessmentId, {
      onSuccess: () => setRevertDialogOpen(false),
    })
  }

  const handleTogglePublish = (categoriaId: string, publicado: boolean) => {
    if (!currentAssessmentId) return
    togglePublishMutation.mutate({
      assessmentId: currentAssessmentId,
      categoriaId,
      publicado,
    })
  }

  // Director category filtering: only show published categories
  const allCategorias = assessment?.template?.categorias ?? []
  const visibleCategorias = isDirector
    ? allCategorias.filter((cat) => assessment?.publicados?.[cat.id] === true)
    : allCategorias
  const hiddenCategorias = isDirector
    ? allCategorias.filter((cat) => assessment?.publicados?.[cat.id] !== true)
    : []

  const isLoading = isLoadingAssessment || isLoadingCalc
  const isEmpty = !isLoading && !assessment

  return (
    <div>
      <PersonPickerBar />
      <div className="p-6 space-y-6">
        {/* Loading state */}
        {isLoading && (
          <div data-testid="metas-loading-skeleton" className="space-y-4">
            <Skeleton className="h-10 w-3/4 rounded-monumenta" />
            <Skeleton className="h-6 w-1/2 rounded-monumenta" />
            <div className="space-y-4 mt-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-40 w-full rounded-monumenta" />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div
            className="text-brand-gray text-center py-12"
            data-testid="metas-empty-state"
          >
            Nenhuma avaliação de bonificação encontrada para este colaborador
          </div>
        )}

        {/* Main content */}
        {!isLoading && assessment && (
          <>
            {/* Page header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate({ to: '/performance/bonificacao' })}
                  className="p-1.5 rounded-md hover:bg-brand-dark-lighter transition-colors text-brand-gray hover:text-white"
                  aria-label="Voltar para lista"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="font-display text-lg text-white">
                    {assessment.template?.name}
                  </h1>
                  <p className="text-sm text-brand-gray mt-0.5">
                    Ciclo {assessment.ciclo}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={assessment.status} />

                {/* Action buttons */}
                {showRevert && (
                  <>
                    <button
                      type="button"
                      className="btn-ghost transition-all duration-200 hover:scale-105 active:scale-95"
                      data-testid="status-revert-btn"
                      onClick={() => setRevertDialogOpen(true)}
                    >
                      Devolver
                    </button>
                    <Dialog open={revertDialogOpen} onOpenChange={setRevertDialogOpen}>
                      <DialogContent className="bg-brand-dark border-brand-gray/20 shadow-monumenta-xl">
                        <DialogHeader>
                          <DialogTitle className="text-white font-display">Devolver avaliação?</DialogTitle>
                          <DialogDescription>
                            O status será revertido para{' '}
                            <strong>{STATUS_LABELS[PREV_STATUS[assessment.status] ?? ''] ?? ''}</strong>.
                            O colaborador poderá editar novamente.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => setRevertDialogOpen(false)}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="btn-danger"
                            data-testid="status-confirm-btn"
                            disabled={revertMutation.isPending}
                            onClick={handleRevert}
                          >
                            {revertMutation.isPending ? 'Revertendo...' : 'Devolver'}
                          </button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}

                {showAdvance && (
                  <>
                    <button
                      type="button"
                      className="btn-primary transition-all duration-200 hover:scale-105 active:scale-95"
                      data-testid="status-advance-btn"
                      onClick={() => setAdvanceDialogOpen(true)}
                    >
                      {ADVANCE_LABELS[assessment.status] ?? 'Avançar'}
                    </button>
                    <Dialog open={advanceDialogOpen} onOpenChange={(open) => {
                      setAdvanceDialogOpen(open)
                      if (!open) setAdvanceValidationError(null)
                    }}>
                      <DialogContent
                        className="bg-brand-dark border-brand-gray/20 shadow-monumenta-xl"
                        data-testid="status-advance-dialog"
                      >
                        <DialogHeader>
                          <DialogTitle className="text-white font-display">Avançar avaliação?</DialogTitle>
                          <DialogDescription>
                            O status será alterado para{' '}
                            <strong>{STATUS_LABELS[NEXT_STATUS[assessment.status] ?? ''] ?? ''}</strong>.
                          </DialogDescription>
                        </DialogHeader>
                        {advanceValidationError && (
                          <p
                            className="text-brand-red text-sm"
                            data-testid="validation-error-advance"
                          >
                            {advanceValidationError}
                          </p>
                        )}
                        <DialogFooter>
                          <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => setAdvanceDialogOpen(false)}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="btn-primary"
                            data-testid="status-confirm-btn"
                            disabled={advanceMutation.isPending}
                            onClick={handleAdvance}
                          >
                            {advanceMutation.isPending
                              ? 'Processando...'
                              : ADVANCE_LABELS[assessment.status] ?? 'Avançar'}
                          </button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
              </div>
            </div>

            {/* Category Accordions */}
            <div className="space-y-4">
              {visibleCategorias.map((categoria) => {
                const catResult = calculateResult?.categorias?.find(
                  (c) => c.id === categoria.id
                )
                const gateInfo = getCategoryGateInfo(categoria.id)
                const isCat3 = categoria.nome.includes('Projeto') || categoria.nome.includes('Individual')
                const okrs = assessment._okrs as Array<{ okrId: string; objective: string; status: string; progress: number; score: number | null; keyResults?: OKRKeyResult[] }> | undefined

                return (
                  <div key={categoria.id}>
                    <CategoryAccordion
                      categoria={categoria}
                      dados={assessment.dados}
                      bonusResult={catResult}
                      readOnly={readOnly}
                      assessmentId={assessment.assessmentId}
                      assessmentStatus={assessment.status as BonusAssessmentStatus}
                      permissions={permissions ?? undefined}
                      onIndicatorUpdate={handleIndicatorUpdate}
                      savingIndicatorId={
                        categoria.indicadores.find((ind) => isSaving(ind.id))?.id
                      }
                      publicado={assessment.publicados?.[categoria.id] ?? false}
                      isRH={isRH}
                      onTogglePublish={handleTogglePublish}
                    >
                      {isCat3 ? (
                        <div className="px-4 pb-4">
                          {(!okrs || okrs.length === 0) ? (
                            <div className="text-center py-8 text-brand-gray">
                              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Nenhum OKR individual cadastrado</p>
                              <p className="text-xs mt-1">Os OKRs são gerenciados na página de Objetivos Individuais</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-medium text-brand-gray">
                                  OKRs Individuais ({okrs.length})
                                </h4>
                                <a
                                  href="/performance/objetivos-individuais"
                                  className="text-xs text-brand-primary hover:underline"
                                >
                                  Gerenciar OKRs &rarr;
                                </a>
                              </div>
                              {okrs.map((okr) => {
                                const isExpanded = expandedOkrs.has(okr.okrId)
                                const krs = okr.keyResults || []
                                return (
                                  <div key={okr.okrId} className="border border-brand-gray/20 rounded-lg overflow-hidden">
                                    {/* OKR Header */}
                                    <div
                                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-brand-dark-lighter/50 transition-colors"
                                      onClick={() => toggleOkr(okr.okrId)}
                                    >
                                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                        okr.status === 'completed' ? 'bg-brand-primary' :
                                        okr.status === 'active' ? 'bg-brand-blue' :
                                        okr.status === 'cancelled' ? 'bg-error' :
                                        'bg-warning'
                                      }`} />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-white truncate">{okr.objective}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            okr.status === 'active' ? 'bg-brand-blue/20 text-brand-blue' :
                                            okr.status === 'completed' ? 'bg-brand-primary/20 text-success' :
                                            okr.status === 'draft' ? 'bg-brand-gray/20 text-brand-gray' :
                                            'bg-error/20 text-error'
                                          }`}>
                                            {okr.status === 'active' ? 'Em andamento' :
                                             okr.status === 'completed' ? 'Concluído' :
                                             okr.status === 'draft' ? 'Rascunho' : 'Cancelado'}
                                          </span>
                                          {okr.score !== null && (
                                            <span className="text-xs text-warning flex items-center gap-1">
                                              ★ {okr.score.toFixed(1)}/5
                                            </span>
                                          )}
                                          {krs.length > 0 && (
                                            <span className="text-xs text-brand-gray">
                                              {krs.length} KR{krs.length !== 1 ? 's' : ''}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        <span className="text-lg font-bold text-white">{okr.progress}%</span>
                                        <div className="w-24 h-1.5 bg-brand-dark-lighter rounded-full mt-1">
                                          <div
                                            className={`h-full rounded-full transition-all ${
                                              okr.progress >= 100 ? 'bg-brand-primary' :
                                              okr.progress >= 70 ? 'bg-brand-blue' :
                                              okr.progress >= 40 ? 'bg-warning' : 'bg-error'
                                            }`}
                                            style={{ width: `${Math.min(okr.progress, 100)}%` }}
                                          />
                                        </div>
                                      </div>
                                      <ChevronDown className={`h-4 w-4 text-brand-gray flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </div>

                                    {/* Expanded: Key Results */}
                                    {isExpanded && (
                                      <div className="border-t border-brand-gray/20 px-4 pb-4">
                                        <div className="flex items-center justify-between py-3">
                                          <span className="text-xs text-brand-gray font-medium">
                                            Key Results ({krs.length})
                                          </span>
                                        </div>
                                        {krs.length === 0 ? (
                                          <p className="text-xs text-brand-gray py-2">
                                            Nenhum resultado-chave cadastrado
                                          </p>
                                        ) : (
                                          <div className="space-y-0">
                                            {krs.map((kr) => (
                                              <div
                                                key={kr.krId}
                                                className="flex items-center gap-3 py-3 border-b border-brand-gray/10 last:border-0"
                                              >
                                                <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                                                  kr.status === 'completed' ? 'bg-brand-primary/20 text-success' :
                                                  kr.status === 'in-progress' ? 'bg-brand-blue/20 text-brand-blue' :
                                                  kr.status === 'at-risk' ? 'bg-warning/20 text-warning' :
                                                  kr.status === 'behind' ? 'bg-error/20 text-error' :
                                                  'bg-brand-gray/20 text-brand-gray'
                                                }`}>
                                                  {kr.status === 'completed' ? 'Concluído' :
                                                   kr.status === 'in-progress' ? 'Em andamento' :
                                                   kr.status === 'at-risk' ? 'Em risco' :
                                                   kr.status === 'behind' ? 'Atrasado' :
                                                   'Não iniciado'}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                  <p className="text-sm text-white/90 truncate">{kr.name}</p>
                                                  <div className="flex items-center gap-3 mt-1 text-xs text-brand-gray">
                                                    {kr.period && (
                                                      <span>
                                                        {new Date(kr.period.startDate).toLocaleDateString('pt-BR')} - {new Date(kr.period.endDate).toLocaleDateString('pt-BR')}
                                                      </span>
                                                    )}
                                                    <span>Peso: {kr.weight}</span>
                                                    {kr.unit && <span>{kr.unit}</span>}
                                                  </div>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                  {kr.averageScore !== null && (
                                                    <span className="text-xs text-warning">
                                                      ★ {kr.averageScore.toFixed(1)}/5
                                                    </span>
                                                  )}
                                                  <span className="text-sm font-medium text-white">
                                                    {kr.currentValue}/{kr.targetValue}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      ) : undefined}
                    </CategoryAccordion>
                  </div>
                )
              })}

              {/* Hidden categories for directors — awaiting publication */}
              {hiddenCategorias.length > 0 && (
                <div className="space-y-2" data-testid="hidden-categories">
                  {hiddenCategorias.map((categoria) => (
                    <div
                      key={categoria.id}
                      className="card flex items-center gap-3 py-3 px-4 opacity-50"
                      data-testid={`hidden-category-${categoria.id}`}
                    >
                      <Lock className="w-4 h-4 text-brand-gray" />
                      <span className="text-brand-gray text-sm">
                        {categoria.nome} — Ainda não tornado público
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bonus Summary Table — show when finalizado OR any category published */}
            {(assessment.status === 'finalizado' ||
              Object.values(assessment.publicados ?? {}).some((v) => v)) &&
              calculateResult && (
              <div className="card mt-6">
                <h2 className="font-display text-lg text-white mb-4">
                  Bônus Calculado
                </h2>
                <BonusSummaryTable
                  bonusResult={calculateResult}
                  salarioBase={assessment.employee?.salary ?? 0}
                  publicados={assessment.publicados}
                  isRH={isRH}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const MetasPageProtected = withPageProtection(MetasPage, PAGES.PERFORMANCE_BONIFICACAO)

export const Route = createFileRoute('/_auth/performance/bonificacao/metas')({
  component: MetasPageProtected,
  validateSearch: (search: Record<string, unknown>): MetasSearch => ({
    employee: search.employee as string | undefined,
  }),
})
